import { MaterialAndSuppliers } from '@/api/queries/project/useGetProjectMaterialListSummaryItems';
import useGetSupplier from '@/api/queries/suppliers/useGetSupplier';
import useGetSuppliers from '@/api/queries/suppliers/useGetSuppliers';
import { humanizeCurrency } from '@/helpers/currency';
import { mergeSelectedIntoOptions } from '@/helpers/selectOptionsHelper';
import { Combobox, SelectOption } from '@payaca/ambrosia';

import { FC, useState } from 'react';

export const MaterialPurchaseRecordSupplierSelectionControl: FC<{
  materialAndSuppliers: MaterialAndSuppliers;
  value: string | null;
  onChange: (supplierId: string | null) => void;
}> = ({ materialAndSuppliers, value, onChange }) => {
  const [query, setQuery] = useState<string>('');
  const { suppliers, isLoading } = useGetSuppliers(
    { searchTerm: query },
    {
      limit: 10,
    },
    query.length > 0
  );
  const { supplier: selectedSupplier } = useGetSupplier(value || undefined);

  let baseOptions: SelectOption<string>[];
  if (query) {
    if (isLoading) {
      baseOptions = [];
    } else {
      baseOptions =
        suppliers?.items.map((supplier) => ({
          label: supplier.name,
          value: supplier.id,
        })) || [];
    }
  } else {
    baseOptions = materialAndSuppliers.material.suppliedBy.map(
      (supplierMaterial) => ({
        label: `${supplierMaterial.supplier.name} (${humanizeCurrency(
          supplierMaterial.price.unitPriceExcTax
        )})`,
        value: supplierMaterial.supplier.id,
      })
    );
  }

  // A selected supplier may fall outside the options (e.g. picked via search
  // but not in the material's suppliedBy list); merge it in so the Combobox
  // can render its label after the query clears.
  const options = mergeSelectedIntoOptions(
    baseOptions,
    selectedSupplier
      ? { label: selectedSupplier.name, value: selectedSupplier.id }
      : undefined
  );
  return (
    <Combobox
      inputPlaceholder="Search across all existing suppliers"
      placeholder="Choose a supplier"
      onChange={(value) => onChange(value as string)}
      value={value || ''}
      query={query}
      setQuery={(val) => {
        setQuery(val);
        // While searching for a new supplier it would not be obvious that the previously
        // set supplier is still set, so we clear it
        onChange(null);
      }}
      options={options}
    />
  );
};
